// client/src/components/map/DisplayMarks.tsx
//
// A truck's overlay marks (ST-00276 phase 2, map colours & states) where the surface is HTML —
// control room chips, the Live View rail and detail panel, the asset card, the legends: one small
// tag per mark, its words beside a glyph whose SHAPE says the mark (a dot = badge, a hollow
// circle = ring, a pulsing dot = pulse, a dashed circle = dashed), in its palette token's colours
// (map/assetStatus classes, which flip with the theme). Colour is never the only cue.

import type { ReactNode } from 'react';
import { ASSET_STATUS_CLASSES, type AssetStatusKey } from './assetStatus';
import { NEUTRAL_MARK, badgeInitial, extraMarks, markColour } from './assetDisplay';
import { MARK_TEXT, type DisplayOverlay, type OverlayMark } from './displayRules';

const NEUTRAL_PILL = 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-300';
const NEUTRAL_DOT = 'bg-slate-600';

/** The glyph of a mark (shape first, colour second). */
export function MarkGlyph({
  mark,
  token,
  className = '',
}: {
  mark: OverlayMark;
  token: AssetStatusKey | string | null;
  className?: string;
}) {
  const c = token ? ASSET_STATUS_CLASSES[token as AssetStatusKey] : null;
  const dot = c ? c.dot : NEUTRAL_DOT;
  switch (mark) {
    case 'ring':
      return (
        <span
          aria-hidden="true"
          data-glyph="ring"
          className={`inline-block w-2.5 h-2.5 rounded-full border-2 bg-transparent border-slate-600 ${className}`}
          // The token's theme-aware solid colour (the --as-* properties of map/assetStatus).
          style={token ? { borderColor: `var(--as-${String(token).replace(/_/g, '-')}-solid)` } : undefined}
        />
      );
    case 'pulse':
      return (
        <span aria-hidden="true" data-glyph="pulse" className={`relative inline-flex w-2.5 h-2.5 ${className}`}>
          <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 motion-safe:animate-ping ${dot}`} />
          <span className={`relative inline-block w-2.5 h-2.5 rounded-full ${dot}`} />
        </span>
      );
    case 'dashed':
      return (
        <span
          aria-hidden="true"
          data-glyph="dashed"
          className={`inline-block w-2.5 h-2.5 rounded-full border border-dashed border-slate-500 ${className}`}
        />
      );
    default:
      return <span aria-hidden="true" data-glyph="badge" className={`inline-block w-2 h-2 rounded-full ${dot} ${className}`} />;
  }
}

/** One mark as a small tag: glyph + words. */
export function MarkTag({ overlay, large = false }: { overlay: DisplayOverlay; large?: boolean }) {
  const c = overlay.token ? ASSET_STATUS_CLASSES[overlay.token as AssetStatusKey] : null;
  const words = overlay.label || MARK_TEXT[overlay.mark].label;
  return (
    <span
      data-mark={overlay.mark}
      data-rule={overlay.rule_id}
      data-token={overlay.token ?? ''}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${
        large ? 'text-xs' : 'text-[10px]'
      } ${c ? c.pill : NEUTRAL_PILL}`}
    >
      <MarkGlyph mark={overlay.mark} token={overlay.token} />
      {words}
    </span>
  );
}

/**
 * A display's marks as tags (the dashed outline is the marker's own, so it is left out), less
 * the rule ids a surface already draws its own way (`skip`).
 */
export default function DisplayMarks({
  overlays,
  skip = [],
  large = false,
  className = '',
}: {
  overlays: readonly DisplayOverlay[] | null | undefined;
  skip?: readonly string[];
  large?: boolean;
  className?: string;
}) {
  const marks = extraMarks(overlays).filter((o) => !skip.includes(o.rule_id));
  if (!marks.length) return null;
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`} data-display-marks="">
      {marks.map((o) => (
        <MarkTag key={o.rule_id} overlay={o} large={large} />
      ))}
    </span>
  );
}

/**
 * A DOM marker with its marks drawn around it (Replay's asset marker): the strongest ring as a
 * coloured ring, a pulse as a ring that pulses (steady with reduced motion), a badge as a dot at
 * the top right. `size` is the marker box in px.
 */
export function MarkerHalo({
  overlays,
  size,
  children,
}: {
  overlays: readonly DisplayOverlay[] | null | undefined;
  size: number;
  children: ReactNode;
}) {
  const marks = extraMarks(overlays);
  const ring = marks.find((o) => o.mark === 'ring');
  const pulse = marks.find((o) => o.mark === 'pulse');
  const badge = marks.find((o) => o.mark === 'badge');
  // As on the live map: the badge's initial, and "+" when there are more marks than drawn.
  const more = marks.length > [ring, pulse, badge].filter(Boolean).length;
  const corner = `${badge ? badgeInitial(badge) : ''}${more ? '+' : ''}`;
  const inset = Math.round(size * 0.12);
  const dot = Math.max(12, Math.round(size * 0.3));
  return (
    <span className="relative inline-block" style={{ width: size, height: size }}>
      {pulse && (
        <span
          aria-hidden="true"
          data-halo="pulse"
          className="absolute rounded-full motion-safe:animate-ping"
          style={{ inset, border: `3px solid ${markColour(pulse)}`, opacity: 0.8 }}
        />
      )}
      {(ring || pulse) && (
        <span
          aria-hidden="true"
          data-halo={ring ? 'ring' : 'pulse-steady'}
          className="absolute rounded-full"
          style={{ inset, border: `3px solid ${markColour((ring || pulse) as DisplayOverlay)}` }}
        />
      )}
      {children}
      {corner && (
        <span
          aria-hidden="true"
          data-halo="badge"
          className="absolute inline-flex items-center justify-center rounded-full border-2 border-white text-white font-bold leading-none"
          style={{
            minWidth: dot,
            height: dot,
            padding: corner.length > 1 ? '0 2px' : 0,
            top: inset - 4,
            right: inset - 4,
            fontSize: Math.round(dot * 0.62),
            background: badge ? markColour(badge) : NEUTRAL_MARK,
          }}
        >
          {corner}
        </span>
      )}
    </span>
  );
}
